import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { formatMoney, PAYMENT_METHODS, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import { api } from '../../lib/api';
import type { SaleRow } from '../../lib/types';
import { endOfDayIso, rangeFor, RANGE_OPTIONS, startOfDayIso, type RangeKey } from '../../lib/dates';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, Button, EmptyState, ErrorNotice, Field, PageHeader, Panel, Select, Spinner, TextInput, formatDateTime } from '../../ui';
import { useDebounced } from '../../ui/inputs';

const PAGE = 50;

export function SalesPage() {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const [range, setRange] = useState<RangeKey>('today');
  const [custom, setCustom] = useState(rangeFor('7d'));
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const dq = useDebounced(q.trim(), 250);
  const { from, to } = rangeFor(range, custom);

  const res = useQuery({
    queryKey: ['sales', branch?.id, from, to, dq, status, page],
    queryFn: () => {
      const p = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
      if (branch) p.set('branchId', branch.id);
      if (!dq) {
        p.set('from', startOfDayIso(from));
        p.set('to', endOfDayIso(to));
      } else p.set('q', dq);
      if (status) p.set('status', status);
      return api.get<{ items: SaleRow[]; total: number }>(`/api/sales?${p}`);
    },
  });
  const cur = me.business.baseCurrency as CurrencyCode;
  const items = res.data?.items ?? [];
  const totalValue = items.filter((s) => s.status === 'completed').reduce((a, s) => a + s.totalCents - s.refundedCents, 0);

  return (
    <>
      <PageHeader title="Sales" description={`Receipts at ${branch?.name ?? 'your branch'}. Open one to reprint it, take a return or void it.`} />
      <Panel>
        <div className="grid gap-3 border-b border-line p-4 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_auto] lg:items-end">
          <Field label="Find a receipt" htmlFor="sale-q">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
              <TextInput id="sale-q" placeholder="Receipt number or payment reference" className="pl-9" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
            </div>
          </Field>
          <Field label="Period" htmlFor="sale-range">
            <Select id="sale-range" value={range} onChange={(e) => { setRange(e.target.value as RangeKey); setPage(0); }} disabled={!!dq}>
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Status" htmlFor="sale-status">
            <Select id="sale-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="voided">Voided</option>
            </Select>
          </Field>
          <div className="text-right text-sm text-muted lg:pb-2.5">
            {res.data && (
              <>
                {res.data.total} sale{res.data.total === 1 ? '' : 's'}
                {!dq && <span className="block font-medium text-ink num">{formatMoney(totalValue, cur)} net on this page</span>}
              </>
            )}
          </div>
          {range === 'custom' && !dq && (
            <div className="grid grid-cols-2 gap-3 sm:col-span-2">
              <Field label="From" htmlFor="sale-from">
                <TextInput id="sale-from" type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
              </Field>
              <Field label="To" htmlFor="sale-to">
                <TextInput id="sale-to" type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
              </Field>
            </div>
          )}
        </div>
        {res.isLoading && <Spinner />}
        {res.error && <div className="p-4"><ErrorNotice error={res.error} onRetry={() => res.refetch()} /></div>}
        {res.data && items.length === 0 && <EmptyState title="No sales found">{dq ? 'Check the receipt number.' : 'Try a longer period.'}</EmptyState>}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Receipt</th>
                  <th className="px-3 py-2.5 font-medium">When</th>
                  <th className="px-3 py-2.5 font-medium">Cashier</th>
                  <th className="px-3 py-2.5 font-medium">Paid by</th>
                  <th className="px-5 py-2.5 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {items.map((s) => (
                  <tr key={s.id} className="hover:bg-ledger-50/60">
                    <td className="px-5 py-3">
                      <Link to={`/sales/${s.id}`} className="font-medium text-ledger-700 hover:underline num">{s.number}</Link>
                      <span className="ml-2">
                        {s.status === 'voided' && <Badge tone="danger">Voided</Badge>}
                        {s.refundedCents > 0 && s.status !== 'voided' && <Badge tone="warn">Returns</Badge>}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-muted num">{formatDateTime(s.createdAt)}</td>
                    <td className="px-3 py-3 text-sm">{s.cashierName}</td>
                    <td className="px-3 py-3 text-sm">{s.methods.map((m) => PAYMENT_METHODS[m as PaymentMethod]?.label.split(' (')[0] ?? m).join(', ')}</td>
                    <td className="px-5 py-3 text-right font-medium num">
                      <span className={s.status === 'voided' ? 'text-faint line-through' : ''}>{formatMoney(s.totalCents, cur)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {res.data && res.data.total > PAGE && (
          <div className="flex items-center justify-between border-t border-line px-5 py-3">
            <Button size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Newer</Button>
            <span className="text-sm text-muted">Page {page + 1} of {Math.ceil(res.data.total / PAGE)}</span>
            <Button size="sm" disabled={(page + 1) * PAGE >= res.data.total} onClick={() => setPage(page + 1)}>Older</Button>
          </div>
        )}
      </Panel>
    </>
  );
}
